import { createElement } from "../utils/dom.js";
import {
    saveToLocalStorage,
    getFromLocalStorage,
    removeFromLocalStorage,
    collectUserMetadata,
} from "../utils/storage.js";
import {
    createModalHeader,
    createEmojiSection,
    createTextSection,
    createSubmitButton,
    createSuccessContent,
    createPoweredBy,
} from "./ui.js";

/**
 * Create feedback modal
 * @param {Object} config - Modal configuration
 * @param {Function} onClose - Close callback
 * @returns {Object} Modal API
 */
export function createModal(config, onClose) {
    const state = {
        feedback: { text: "", emoji: "" },
        isSubmitting: false,
        isSubmitted: false,
        clickOutsideHandler: null,
    };

    const modal = createElement("div", "feedback-modal modal-fade-in", {
        role: "dialog",
        "aria-modal": "true",
    });

    const elements = buildModalElements(config, state, onClose);
    appendElementsToModal(modal, elements);
    setupModalEventListeners(modal, elements, state, config, onClose);
    loadSavedFeedback(elements.textarea, state);

    return {
        element: modal,
        close: () => closeModal(modal, onClose),
    };
}

/**
 * Build all modal elements
 * @param {Object} config - Configuration
 * @param {Object} state - Modal state
 * @param {Function} onClose - Close callback
 * @returns {Object} Modal elements
 */
function buildModalElements(config, state, onClose) {
    const header = createModalHeader(config, onClose);

    const questionSection = createElement("div", "feedback-question", {
        innerHTML: `<p>${config.questionText}</p>`,
    });

    const emojiSection = createEmojiSection(config, (emoji, button) => {
        handleEmojiSelection(emoji, button, state);
    });

    const {
        section: textSection,
        textarea,
        errorDiv,
    } = createTextSection(config, (text) =>
        handleTextChange(text, state, errorDiv)
    );

    const { container: submitContainer, button: submitButton } =
        createSubmitButton(config, () =>
            handleSubmit(
                state,
                config,
                { textarea, errorDiv, submitButton },
                onClose
            )
        );

    const poweredBy = createPoweredBy();

    const content = createElement("div", "feedback-content");
    content.appendChild(questionSection);
    content.appendChild(emojiSection);
    content.appendChild(textSection);
    content.appendChild(submitContainer);
    content.appendChild(poweredBy);

    return {
        header,
        content,
        textarea,
        errorDiv,
        submitButton,
        emojiSection,
    };
}

/**
 * Append elements to modal
 * @param {HTMLElement} modal - Modal element
 * @param {Object} elements - Modal elements
 */
function appendElementsToModal(modal, elements) {
    modal.appendChild(elements.header);
    modal.appendChild(elements.content);
}

/**
 * Setup modal event listeners
 * @param {HTMLElement} modal - Modal element
 * @param {Object} elements - Modal elements
 * @param {Object} state - Modal state
 * @param {Object} config - Configuration
 * @param {Function} onClose - Close callback
 */
function setupModalEventListeners(modal, elements, state, config, onClose) {
    // Escape key handler
    const handleEscapeKey = (e) => {
        if (e.key === "Escape" && !state.isSubmitting) {
            document.removeEventListener("keydown", handleEscapeKey);
            cleanupClickOutside(state);
            onClose();
        }
    };

    document.addEventListener("keydown", handleEscapeKey);

    // // Click outside handler with proper delay
    // const handleClickOutside = (e) => {
    //     if (modal && !modal.contains(e.target)) {
    //         cleanupClickOutside(state);
    //         onClose();
    //     }
    // };

    // Store reference and add with delay
    // state.clickOutsideHandler = handleClickOutside;
    // setTimeout(() => {
    //     if (state.clickOutsideHandler) {
    //         document.addEventListener("click", state.clickOutsideHandler);
    //     }
    // }, 200);

    // Focus textarea after modal opens
    setTimeout(() => {
        elements.textarea?.focus();
    }, 100);
}

/**
 * Clean up click outside event listener
 * @param {Object} state - Modal state
 */
function cleanupClickOutside(state) {
    if (state.clickOutsideHandler) {
        document.removeEventListener("click", state.clickOutsideHandler);
        state.clickOutsideHandler = null;
    }
}

/**
 * Handle emoji selection
 * @param {string} emoji - Selected emoji
 * @param {HTMLElement} button - Emoji button
 * @param {Object} state - Modal state
 */
function handleEmojiSelection(emoji, button, state) {
    const emojiButtons = button.parentElement.querySelectorAll(".emoji-button");

    if (state.feedback.emoji === emoji) {
        state.feedback.emoji = "";
        button.classList.remove("active");
    } else {
        emojiButtons.forEach((btn) => btn.classList.remove("active"));
        button.classList.add("active");
        state.feedback.emoji = emoji;
    }
}

/**
 * Handle text input changes
 * @param {string} text - Input text
 * @param {Object} state - Modal state
 * @param {HTMLElement} errorDiv - Error display element
 */
function handleTextChange(text, state, errorDiv) {
    if (text.length > 500) {
        showError(errorDiv, "Feedback text cannot exceed 500 characters.");
        return;
    }

    hideError(errorDiv);
    state.feedback.text = text;
    saveToLocalStorage("feedback-widget-text", text);
}

/**
 * Load saved feedback from storage
 * @param {HTMLElement} textarea - Textarea element
 * @param {Object} state - Modal state
 */
function loadSavedFeedback(textarea, state) {
    const savedText = getFromLocalStorage("feedback-widget-text", "");
    if (savedText && textarea) {
        textarea.value = savedText;
        state.feedback.text = savedText;
    }
}

/**
 * Handle form submission
 * @param {Object} state - Modal state
 * @param {Object} config - Configuration
 * @param {Object} elements - Form elements
 * @param {Function} onClose - Close callback
 */
async function handleSubmit(state, config, elements, onClose) {
    if (state.isSubmitting) return;

    // Validation
    if (!config.projectSecret) {
        showError(elements.errorDiv, "Project secret is missing.");
        return;
    }

    if (!state.feedback.text.trim()) {
        showError(elements.errorDiv, "Feedback text is required.");
        return;
    }

    hideError(elements.errorDiv);
    state.isSubmitting = true;
    updateSubmitButton(elements.submitButton, true);
    // setTimeout(() => {
    //     showSuccessState(
    //         elements.submitButton.closest(".feedback-content"),
    //         config
    //     );
    //     state.isSubmitted = true;
    //     removeFromLocalStorage("feedback-widget-text");
    //     state.isSubmitting = false;

    //     if (config.autoCloseAfterSubmit) {
    //         setTimeout(() => onClose(), 3000);
    //     }
    // }, 1000);
    // return;
    try {
        const success = await submitFeedback(state.feedback, config);

        if (success) {
            state.isSubmitted = true;
            removeFromLocalStorage("feedback-widget-text");
            showSuccessState(
                elements.submitButton.closest(".feedback-content"),
                config
            );

            if (config.autoCloseAfterSubmit) {
                setTimeout(() => onClose(), 3000);
            }
        }
    } catch (error) {
        console.error("Error submitting feedback:", error);
        showError(
            elements.errorDiv,
            error.message || "An error occurred while submitting feedback."
        );
    } finally {
        state.isSubmitting = false;
        updateSubmitButton(elements.submitButton, false, config.submitText);
    }
}

/**
 * Submit feedback to API
 * @param {Object} feedback - Feedback data
 * @param {Object} config - Configuration
 * @returns {Promise<boolean>} Success status
 */
async function submitFeedback(feedback, config) {
    const metadata = await collectUserMetadata();

    const response = await fetch(config.apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            projectSecret: config.projectSecret,
            feedback: {
                text: feedback.text,
                ...(feedback.emoji ? { emoji: feedback.emoji } : {}),
            },
            metadata,
        }),
    });

    if (!response.ok) {
        const errorData = await response.json();

        if (response.status === 401) {
            throw new Error(
                "Authentication failed. Please check your project secret."
            );
        }

        if (errorData?.code === "LIMIT_REACHED") {
            throw new Error(
                "The author has run out of free credits and can't accept more feedback at this time."
            );
        }
        throw new Error(
            errorData?.message ||
                "Failed to submit feedback. Please try again later."
        );
    }

    return true;
}

/**
 * Update submit button state
 * @param {HTMLElement} button - Submit button
 * @param {boolean} isLoading - Loading state
 * @param {string} text - Button text
 */
function updateSubmitButton(button, isLoading, text = "Submit") {
    if (isLoading) {
        button.disabled = true;
        button.innerHTML = `
      <div class="loading-spinner"></div>
      <span>Submitting...</span>
    `;
    } else {
        button.disabled = false;
        button.innerHTML = `<span class="button-text">${text}</span>`;
    }
}

/**
 * Show success state
 * @param {HTMLElement} contentElement - Content container
 * @param {Object} config - Configuration
 */
function showSuccessState(contentElement, config) {
    // Add fade-out animation to current content
    contentElement.style.opacity = "0";
    contentElement.style.transform = "translateY(-10px)";
    contentElement.style.transition = "opacity 0.3s ease, transform 0.3s ease";

    setTimeout(() => {
        const successContent = createSuccessContent(config);
        const poweredBy = createPoweredBy();

        contentElement.innerHTML = "";
        contentElement.appendChild(successContent);
        contentElement.appendChild(poweredBy);

        // Reset and animate in
        contentElement.style.opacity = "1";
        contentElement.style.transform = "translateY(0)";
    }, 300);
}
/**
 * Show error message
 * @param {HTMLElement} errorDiv - Error container
 * @param {string} message - Error message
 */
function showError(errorDiv, message) {
    if (errorDiv) {
        errorDiv.textContent = message;
        errorDiv.style.display = "block";
    }
}

/**
 * Hide error message
 * @param {HTMLElement} errorDiv - Error container
 */
function hideError(errorDiv) {
    if (errorDiv) {
        errorDiv.style.display = "none";
    }
}

/**
 * Close modal with animation
 * @param {HTMLElement} modal - Modal element
 * @param {Function} onClose - Close callback
 */
function closeModal(modal, onClose) {
    modal.classList.remove("modal-fade-in");
    modal.classList.add("modal-fade-out");
    setTimeout(() => {
        onClose();
    }, 300);
}
